import React, { useEffect, useState } from "react";
import {
  Modal,
  Group,
  Text,
  Stack,
  Paper,
  Button,
  ScrollArea,
  Divider,
} from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";

interface ResourceFile {
  key: string;
}

interface Props {
  isOpen: boolean;
  batchNo: string | null;
  onClose: () => void;
}

const ViewResourcesModal: React.FC<Props> = ({
  isOpen,
  batchNo,
  onClose,
}) => {
  const [resources, setResources] = useState<Record<string, ResourceFile[]>>(
    {}
  );
  const [loading, setLoading] = useState(false);

  
  useEffect(() => {
    if (isOpen && batchNo) fetchResources();
  }, [isOpen, batchNo]);

 const fetchResources = async () => {
  try {
    setLoading(true);

    const res = await fetch(
      `https://trainer-portal-78f5.onrender.com/api/lms/resources?batchNo=${encodeURIComponent(
        batchNo!
      )}`
    );

    const json = await res.json();
    console.log("Resources API response:", json);

    // ✅ Normalize response
    const files = Array.isArray(json)
      ? json
      : json.files || [];

    const grouped: Record<string, ResourceFile[]> = {};

    files.forEach((item: any) => {
      const key = item.key || item.Key;
      if (!key) return;

      const parts = key.split("/");
      const folder = parts[2] || "General";

      if (!grouped[folder]) grouped[folder] = [];
      grouped[folder].push({ key });
    });

    setResources(grouped);
  } catch (error) {
    console.error(error);
    showNotification({
      title: "Error",
      message: "Failed to fetch resources",
      color: "red",
    });
  } finally {
    setLoading(false);
  }
};


  const downloadFile = async (key: string) => {
    window.open(
      `https://trainer-portal-78f5.onrender.com/api/lms/download?key=${encodeURIComponent(
        key
      )}`,
      "_blank"
    );
  };


const cleanFileName = (key: string) => {
  if (!key) return 'Unknown File';

  const fileName = key.split('/').pop() || '';

  // Remove EVERYTHING before the actual filename (date + time + uuid)
  // Stops exactly at the UUID underscore
  const cleaned = fileName.replace(
    /^.*?[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}_/i,
    ''
  );

  return cleaned || fileName;
};


  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={`Resources – Batch ${batchNo}`}
      size="lg"
      centered
    >
      <ScrollArea h={400}>
        {loading ? (
          <Text size="sm">Loading...</Text>
        ) : Object.keys(resources).length === 0 ? (
          <Text size="sm" c="dimmed">
            No resources found
          </Text>
        ) : (
          <Stack>
            {Object.entries(resources).map(([folder, files]) => (
              <Paper key={folder} withBorder p="sm">
                <Text fw={600} size="sm">
                  📁 {folder}
                </Text>

                <Divider my="xs" />

                <Stack gap="xs">
                  {files.map((file) => (
                    <Group
                      key={file.key}
                      justify="space-between"
                      wrap="nowrap"
                    >
                      <Text size="sm" truncate>
                        {cleanFileName(file.key)}
                      </Text>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconDownload size={14} />}
                        onClick={() => downloadFile(file.key)}
                      >
                        Download
                      </Button>
                    </Group>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </ScrollArea>
    </Modal>
  );
};

export default ViewResourcesModal;

